import type { CloseReason, VulnStatus } from '../../../modules/types/vulnerabilities'

/**
 * Mirrors `REOPEN_ON_DETECTION` in apps/scan/services/finding_status.py. The
 * reason is not a label: it decides whether a later scan that sees the finding
 * again brings it back. Kept as data here for the same reason the backend keeps
 * it as data — so the hint the user reads is derived from the policy rather
 * than written next to it and left to drift.
 */
export const REOPENS_ON_DETECTION: Record<CloseReason, boolean> = {
  fixed: true,
  not_detected: true,
  false_positive: false,
  accepted_risk: false,
  // A won't-fix marked as a duplicate of another finding: a standing human judgement.
  duplicate: false,
}

const CLOSE_REASON_LABEL_KEY: Record<CloseReason, string> = {
  fixed: 'VULN.CLOSE_REASON.FIXED',
  not_detected: 'VULN.CLOSE_REASON.NOT_DETECTED',
  false_positive: 'VULN.CLOSE_REASON.FALSE_POSITIVE',
  accepted_risk: 'VULN.CLOSE_REASON.ACCEPTED_RISK',
  duplicate: 'VULN.CLOSE_REASON.DUPLICATE',
}

export function closeReasonLabelKey(reason: CloseReason): string {
  return CLOSE_REASON_LABEL_KEY[reason]
}

/** What a re-detection will do to a finding closed for this reason. */
export function reopenHintKey(reason: CloseReason): string {
  return REOPENS_ON_DETECTION[reason]
    ? 'VULN.CLOSE_REASON.HINT_REOPENS'
    : 'VULN.CLOSE_REASON.HINT_LATCHED'
}

/**
 * How the finding's CURRENT state reads. A closed finding is named by its
 * reason, not by the bare word "closed" — "Fixed" and "False positive" are
 * different futures, and hiding that behind one label is the ambiguity the
 * reason exists to remove. `VULN.STATUS.CLOSED` is only a fallback for a row
 * whose reason is missing, which the DB check constraint should prevent.
 */
export function currentLabelKey(status: VulnStatus, closeReason: CloseReason | null): string {
  if (status === 'open') return 'VULN.STATUS.OPEN'
  return closeReason ? CLOSE_REASON_LABEL_KEY[closeReason] : 'VULN.STATUS.CLOSED'
}

/**
 * How a status LOOKS, as one decision made once, so every surface that colours a
 * finding's status renders the same tone in its own vocabulary.
 *
 * The split between the two closed tones is the policy, not a per-reason
 * opinion: a finding closed as `fixed` or `not_detected` is gone but comes back
 * if a scanner sees it again (success), one closed as `false_positive` or
 * `accepted_risk` is a standing human judgement no future scan overturns
 * (secondary).
 */
export type StatusTone = 'open' | 'reopening' | 'latched'

export function statusTone(status: VulnStatus, closeReason: CloseReason | null): StatusTone {
  if (status === 'open') return 'open'
  return closeReason && REOPENS_ON_DETECTION[closeReason] ? 'reopening' : 'latched'
}
